import { DateFormatType, formatNullableDate } from "~/common/utils/DateFormatter";
import Parameter from "~/common/components/Parameter";
import { InfoBall } from "~/features/configurator/Linear/components/LinearInfo";
import { useBooleanState } from "~/common/hooks/useBooleanState";
import { useTranslation } from "react-i18next";

import classes from "~/features/orders/quoteDetails/components/v2/QuoteDetailsSummary.module.less";
import classNames from "classnames";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import type { QuoteDetailsMO } from "~/features/orders/common/components/v2/models";
import { QuoteIsBundleInfoModal } from "~/features/orders/quoteDetails/QuoteIsBundleInfoModal";

interface OrderSummaryProps {
  className?: string;
  quote: QuoteDetailsMO;
}

export const QuoteDetailsSummary = ({ quote, className }: OrderSummaryProps) => {
  const { t } = useTranslation();

  const { val: showBundlePopup, setTrue: openBundlePopup, setFalse: closeBundlePopup } = useBooleanState(false);

  const canSeeComments = userHasPermission(Permission.ADD_VIEW_HEADER_LEVEL_COMMENTS);
  const showComment = quote.comment && canSeeComments;

  return (
    <div className={classNames(classes.root, className)}>
      <div className={classes.params}>
        <Parameter data-test-id={"quote-status"} label={t("quoteDetailsPage.summary.quoteStatus")}>
          {t(`codes.quoteStatus.${quote.status}`)}
        </Parameter>
        <Parameter data-test-id={"order-attainment"} label={t("quoteDetailsPage.summary.orderAttainment")}>
          {quote.orderAttainment}
        </Parameter>
        <Parameter data-test-id={"date-entered"} label={t("quoteDetailsPage.summary.dateEntered")}>
          {formatNullableDate(quote.datePlaced, DateFormatType.DATE)}
        </Parameter>

        <Parameter data-test-id={"account-number"} label={t("quoteDetailsPage.summary.accNo")}>
          {quote.accNo}
        </Parameter>
        <Parameter data-test-id={"order-fullfilment"} label={t("quoteDetailsPage.summary.orderFullfilment")}>
          {quote.orderFulfilment}
        </Parameter>
        <Parameter data-test-id={"date-expired"} label={t("quoteDetailsPage.summary.dateExpired")}>
          {formatNullableDate(quote.dateExpired, DateFormatType.DATE)}
        </Parameter>

        <Parameter data-test-id={"customer-ref-number"} label={t("quoteDetailsPage.summary.customerRefNo")}>
          {quote.poNumber}
        </Parameter>
        <Parameter data-test-id={"quote-number"} label={t("quoteDetailsPage.summary.quoteNumber")}>
          {quote.soNumber}
        </Parameter>
        <Parameter data-test-id={"quote-type"} label={t("quoteDetailsPage.summary.quoteType")}>
          <div className="d-flex flex-align-center">
            {t(quote.quoteType)}
            {quote.isBundledQuote && <InfoBall className={"icon-margin-l"} onClick={openBundlePopup} />}
            {showBundlePopup && <QuoteIsBundleInfoModal onClose={closeBundlePopup} />}
          </div>
        </Parameter>
      </div>
      {showComment && (
        <div className={classes.comment}>
          <Parameter data-test-id={"comment"} label={t("order.information.header.comment")}>
            {quote.comment}
          </Parameter>
        </div>
      )}
    </div>
  );
};
